import React, { useMemo, useState } from 'react';
import { ArrowLeft, ArrowRight, ChevronDown, ShieldAlert, Info, AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import Avatar from '@/components/common/Avatar';
import CircularProgress from '@/components/common/CircularProgress';
import type { BajaPaciente, Decision, Paso2State, PlanPaciente } from '../../../hooks/api/baja';

const DECISION_OPTIONS: { value: Decision; emoji: string; title: string; desc: string }[] = [
  {
    value: 'acompanar',
    emoji: '✅',
    title: 'Acompañar hasta el alta natural',
    desc: 'Continuás acompañando al paciente dentro de Psi Mammoliti hasta el cierre natural del proceso terapéutico.',
  },
  {
    value: 'derivar',
    emoji: '🔄',
    title: 'Derivar a otra psicóloga del equipo',
    desc: 'Implica coordinar el traspaso en conjunto con el equipo de salud mental, asegurando la continuidad del proceso y respetando los tiempos terapéuticos del paciente.',
  },
  {
    value: 'alta',
    emoji: '🏁',
    title: 'Otra situación',
    desc: 'El equipo evaluará este caso en detalle en la reunión de salida.',
  },
];

function planCompleto(plan: PlanPaciente | undefined): boolean {
  if (!plan?.decision) return false;
  if (plan.decision === 'alta') return !!plan.justificacion?.trim();
  return true;
}

interface Props {
  patients: BajaPaciente[];
  loadingPatients: boolean;
  initial: Paso2State | null;
  onBack: () => void;
  onNext: (state: Paso2State) => void;
}

export const Paso2Procesos: React.FC<Props> = ({ patients, loadingPatients, initial, onBack, onNext }) => {
  const [planes, setPlanes] = useState<Record<string, PlanPaciente>>(initial?.planes ?? {});
  const [openId, setOpenId] = useState<string | null>(patients[0]?.id ?? null);
  const [disclosureOpen, setDisclosureOpen] = useState(!!initial?.disclosureOpen);
  const [disclosureText, setDisclosureText] = useState(initial?.disclosureText ?? '');
  const [showError, setShowError] = useState(false);

  const updatePlan = (id: string, patch: Partial<PlanPaciente>) => {
    setPlanes((prev) => {
      const base: PlanPaciente = prev[id] ?? { decision: 'acompanar' };
      return { ...prev, [id]: { ...base, ...patch } };
    });
  };

  const resumen = useMemo(() => {
    let declarados = 0, derivaciones = 0, altas = 0, acompanamiento = 0;
    for (const p of patients) {
      const plan = planes[p.id];
      if (planCompleto(plan)) declarados++;
      if (plan?.decision === 'derivar') derivaciones++;
      if (plan?.decision === 'alta') altas++;
      if (plan?.decision === 'acompanar') acompanamiento++;
    }
    return { declarados, derivaciones, altas, acompanamiento };
  }, [planes, patients]);

  const sinProcesos = !loadingPatients && patients.length === 0;
  const allComplete = sinProcesos ? true : patients.length > 0 && resumen.declarados === patients.length;
  const disclosureInvalido = disclosureOpen && disclosureText.trim().length === 0;

  const handleContinue = () => {
    if (!allComplete || disclosureInvalido) { setShowError(true); return; }
    onNext({
      planes,
      disclosureOpen,
      disclosureText: disclosureText || undefined,
      pacientes_snapshot: patients.map((p) => ({ id: p.id, nombre: p.nombre })),
    });
  };

  return (
    <div className="mx-auto w-full max-w-3xl space-y-8 pb-28">
      <header className="space-y-2">
        <h1 className="text-2xl sm:text-3xl font-semibold leading-tight text-gray-900">Tus procesos terapéuticos activos</h1>
        <p className="text-sm text-muted-foreground">
          Por cada proceso debés indicar qué sucederá con ese paciente durante tu salida. Esta declaración es parte de
          tus obligaciones contractuales.
        </p>
      </header>

      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5 space-y-2">
        <div className="flex items-center gap-2.5">
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-amber-100 text-amber-600 shrink-0">
            <ShieldAlert className="h-4 w-4" />
          </span>
          <h2 className="text-base font-bold text-amber-900">La continuidad de cada proceso es parte del compromiso asumido</h2>
        </div>
        <p className="text-sm leading-relaxed text-amber-900/80">
          Los pacientes que te fueron asignados tienen derecho a una transición cuidadosa, independientemente de los
          motivos o los tiempos de tu salida. La baja no interrumpe automáticamente los procesos activos. Completar
          este paso es lo que hace posible una salida ordenada para vos y una atención continua para quienes están en
          proceso.
        </p>
      </div>

      <div className="rounded-2xl border border-sky-200 bg-sky-50 p-5 space-y-3">
        <div className="flex items-center gap-2.5">
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-sky-100 text-sky-600 shrink-0">
            <Info className="h-4 w-4" />
          </span>
          <h2 className="text-base font-bold text-sky-900">Importante: sobre la comunicación a tus pacientes</h2>
        </div>
        <p className="text-sm leading-relaxed text-sky-900/80">
          Hasta que no completemos juntos este proceso y tengamos la reunión de salida, ninguno de tus pacientes va a
          ser informado de tu decisión — ni por vos, ni por nosotros. Te pedimos que, por ahora, no adelantes nada en
          tus sesiones.
        </p>
        {!disclosureOpen ? (
          <button type="button" onClick={() => setDisclosureOpen(true)} className="text-sm text-sky-700 hover:text-sky-900 underline underline-offset-2">
            ¿Ya hablaste con algún paciente sobre tu decisión? Hacé click acá.
          </button>
        ) : (
          <div className="space-y-2">
            <label className="text-sm text-sky-900">
              Contanos con quién hablaste y qué le dijiste. <span className="text-coral">*</span>
            </label>
            <Textarea
              value={disclosureText}
              onChange={(e) => setDisclosureText(e.target.value)}
              placeholder="Ej: Le mencioné a Lucía M. que estaba evaluando algunos cambios, sin dar detalles..."
              rows={4}
              className="bg-white"
            />
            {showError && disclosureInvalido && <p className="text-xs text-coral">Completá este campo antes de continuar.</p>}
            <button type="button" onClick={() => { setDisclosureOpen(false); setDisclosureText(''); }} className="text-xs text-sky-700 hover:text-sky-900 underline underline-offset-2">
              No hablé con nadie todavía — cerrar
            </button>
          </div>
        )}
      </div>

      <section className="space-y-3">
        {loadingPatients && (
          <div className="flex items-center gap-2 rounded-2xl border border-border bg-card px-4 py-6 text-sm text-muted-foreground">
            <CircularProgress sizeRem={1} /> Cargando tus pacientes…
          </div>
        )}
        {sinProcesos && (
          <div className="rounded-2xl border border-primary/25 bg-primary/5 px-5 py-6 space-y-2">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-primary" />
              <h3 className="text-sm font-semibold text-gray-900">No tenés procesos activos para declarar</h3>
            </div>
            <p className="text-sm leading-relaxed text-gray-700">
              No hay transiciones clínicas que coordinar ni historias clínicas pendientes de traspaso. Podés continuar
              con los pasos siguientes para cerrar formalmente tu vínculo con el equipo.
            </p>
          </div>
        )}

        {patients.map((p) => {
          const plan = planes[p.id];
          const isOpen = openId === p.id;
          const completo = planCompleto(plan);
          return (
            <article key={p.id} className={['rounded-2xl border bg-card shadow-sm', completo ? 'border-primary/50' : 'border-border'].join(' ')}>
              <button type="button" onClick={() => setOpenId(isOpen ? null : p.id)} className="w-full text-left px-4 sm:px-5 py-4 flex items-center gap-3">
                <Avatar name={p.nombre} sizeRem={2.5} className="shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-bold text-base text-gray-900">{p.nombre}</h3>
                    <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border border-border text-muted-foreground">
                      {p.estado}
                    </span>
                    {completo && (
                      <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700">
                        Declarado
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{p.antiguedad} · {p.frecuencia} · Última sesión {p.ultimaSesion}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {p.sesionesRealizadas} {p.sesionesRealizadas === 1 ? 'sesión realizada' : 'sesiones realizadas'} · Próxima: {p.proximaSesion}
                  </p>
                </div>
                <ChevronDown className={['h-5 w-5 text-gray-400 transition-transform shrink-0', isOpen ? 'rotate-180' : ''].join(' ')} />
              </button>

              {isOpen && (
                <div className="px-4 sm:px-5 pb-5 pt-1 space-y-4 border-t border-border">
                  <fieldset className="space-y-2 pt-4">
                    <legend className="text-sm font-medium text-gray-900">
                      ¿Qué harás con este proceso? <span className="text-coral">*</span>
                    </legend>
                    <div className="grid gap-2">
                      {DECISION_OPTIONS.map((opt) => {
                        const selected = plan?.decision === opt.value;
                        return (
                          <label
                            key={opt.value}
                            className={[
                              'flex items-start gap-3 rounded-xl border cursor-pointer transition-colors py-3 px-4',
                              selected ? 'border-primary bg-primary/5' : 'border-border bg-white hover:bg-muted',
                            ].join(' ')}
                          >
                            <input
                              type="radio"
                              name={`decision-${p.id}`}
                              className="sr-only"
                              checked={selected}
                              onChange={() => updatePlan(p.id, { decision: opt.value })}
                            />
                            <span className={['mt-0.5 h-4 w-4 rounded-full border shrink-0', selected ? 'border-primary bg-primary' : 'border-gray-300'].join(' ')} />
                            <div className="flex-1">
                              <p className="text-sm font-medium text-gray-900 flex items-center gap-2">
                                <span aria-hidden>{opt.emoji}</span> {opt.title}
                              </p>
                              <p className="text-xs text-muted-foreground mt-0.5">{opt.desc}</p>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  </fieldset>

                  {plan?.decision === 'alta' && (
                    <div className="space-y-3">
                      <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
                        <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-amber-500" />
                        <p className="text-xs text-amber-900 leading-relaxed">
                          Esta situación será revisada por el equipo en la reunión de salida. En el paso siguiente vas
                          a poder detallar el contexto del proceso.
                        </p>
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm text-gray-700">
                          Describí brevemente la situación <span className="text-coral">*</span>
                        </label>
                        <Textarea
                          value={plan.justificacion ?? ''}
                          maxLength={2000}
                          onChange={(e) => updatePlan(p.id, { justificacion: e.target.value })}
                          placeholder="Explicá brevemente qué está pasando con este proceso y por qué no aplica ninguna de las otras opciones."
                          rows={4}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </article>
          );
        })}
      </section>

      {patients.length > 0 && (
        <div className="rounded-2xl border border-border bg-card px-4 sm:px-5 py-4 flex flex-wrap items-center gap-x-4 gap-y-2">
          <span
            className={[
              'text-xs font-semibold px-2.5 py-1 rounded-full shrink-0',
              allComplete ? 'bg-emerald-50 text-emerald-700' : 'bg-muted text-gray-500',
            ].join(' ')}
          >
            Declarados: {resumen.declarados} / {patients.length}
          </span>
          <div className="text-xs sm:text-sm text-gray-600 flex flex-wrap items-center gap-x-3 gap-y-1">
            <span>✅ Acompañamiento: {resumen.acompanamiento}</span>
            <span>🔄 Derivaciones: {resumen.derivaciones}</span>
            <span>🏁 Otras: {resumen.altas}</span>
          </div>
        </div>
      )}

      <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-between pt-4 border-t border-border">
        <Button type="button" variant="ghost" onClick={onBack}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Volver
        </Button>
        <Button type="button" disabled={!allComplete} onClick={handleContinue} className="sm:min-w-[240px]">
          {loadingPatients ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
          Siguiente: Plan de transición <ArrowRight className="h-4 w-4 ml-1" />
        </Button>
      </div>
    </div>
  );
};

export default Paso2Procesos;
